"use client";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button";
import Dropdown from "../../../components/Dropdown";
import FormInput from "../../../components/FormInput";
import styles from "../css/filter-patient-script.module.css";
import { fetchProfileInfo, fetchProfileFamily, fetchPatientScripts } from "../../../api/dispensary/script/processScriptApi.js";
import { fetchPatientClinicScripts } from "../../../api/dispensary/script/clinicScriptApi.js";
import { useAuth } from "../../../context/AuthContext";

// This will be used by other components
let initialScriptData = [];

// Update function to refresh data globally
export const updateInitialScriptData = (data) => {
  initialScriptData = data;
};

export { initialScriptData };

export default function FilterPatientScript({
  hideNewScriptButton = false,
  hideMedicationHistoryButton = false,
  extraColumns = [],
  columnsToHide = [],
  actionNavigatePath = "/customer/dispensary/script/details",
  actionNavigateResolver = null,
  containerClassName = "",
  filterRowClassName = "",
  hideOtc = false,
  clinicOnly = false, // New prop to filter only clinic scripts
  hideAmended = false,
  tableHeaders = {
    scriptNo: "Script No",
    date: "Date",
    action: "Action",
  },
}) {
  const { profileNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location?.state || {};
  const { user } = useAuth();
  const userBranchCode = user?.branchcode != null ? String(user.branchcode) : "";
  const [medicalAidProvider, setMedicalAidProvider] = useState("");
  const [medicalAidNumber, setMedicalAidNumber] = useState("");
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState("");
  const [mainPatientId, setMainPatientId] = useState(null);
  const [familyData, setFamilyData] = useState([]);
  const [patientEnableBeneficiary, setPatientEnableBeneficiary] = useState(false);
  const [mainMemberEnabled, setMainMemberEnabled] = useState(false);

  const [scriptNoFilter, setScriptNoFilter] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const formatDateTimeZA = (val) => {
    if (!val) return "";
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return "";
      const simple = s.match(/^\d{4}-\d{2}-\d{2}$/);
      if (simple) return simple[0];
    }
    try {
      const d = val instanceof Date ? val : new Date(val);
      if (Number.isNaN(d.getTime())) return "";

      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Africa/Johannesburg',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(d);

      const get = (type) => parts.find(p => p.type === type)?.value || '';
      const yyyy = get('year');
      const mm = get('month');
      const dd = get('day');
      const hh = get('hour');
      const mi = get('minute');
      const ss = get('second');
      const datePart = (yyyy && mm && dd) ? `${yyyy}-${mm}-${dd}` : '';
      const timePart = (hh && mi && ss) ? `${hh}:${mi}:${ss}` : '';
      return [datePart, timePart].filter(Boolean).join(' ');
    } catch {
      return "";
    }
  };

  // Fetch profile info + family
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);

        const info = await fetchProfileInfo(profileNo);

        const { medicalAidProvider, medicalAidNumber, mainPatient, enabled } = info;
        console.log("[FilterPatientScript] Loaded profile info", { profileNo, info });
        setMedicalAidProvider(medicalAidProvider);
        setMedicalAidNumber(medicalAidNumber);
        setMainMemberEnabled(enabled);
        const mainIdStr = mainPatient.id?.toString?.() ?? String(mainPatient.id);
        setMainPatientId(mainIdStr);

        const family = await fetchProfileFamily(profileNo);

        const sanitizedLabels = family.map((f) =>
          String(f.label || '')
            .replace(/\s*\(\s*beneficiary\s*\)\s*/ig, ' ')
            .replace(/\s*beneficiary\s*/ig, ' ')
            .replace(/\s+/g, ' ')
            .trim()
        );

        const labelCounts = sanitizedLabels.reduce((acc, lbl) => {
          acc[lbl] = (acc[lbl] || 0) + 1;
          return acc;
        }, {});

        const options = family.map((f, idx) => {
          const baseLabel = sanitizedLabels[idx];
          const needsDisambiguation = (labelCounts[baseLabel] || 0) > 1;
          return {
            value: f.id.toString(),
            label: needsDisambiguation ? `${baseLabel} (${f.id})` : baseLabel,
          };
        });
        console.log("[FilterPatientScript] Family options", { profileNo, options });
        setPatientOptions(options);
        setFamilyData(family);

        // If a patientId was passed via navigation state, prefer it (e.g. when opening a beneficiary)
        const preferredPatientId = navState?.patientId ? String(navState.patientId) : null;
        const hasPreferredInFamily = preferredPatientId && family.some((f) => String(f.id) === preferredPatientId);
        const nextSelected = hasPreferredInFamily ? preferredPatientId : mainIdStr;

        console.log("[FilterPatientScript] Defaulting selectedPatient", {
          mainPatientId: mainIdStr,
          preferredPatientId,
          chosen: nextSelected,
        });

        setSelectedPatient(nextSelected);

        // Set initial enable state for the selected patient
        const selected = family.find(f => String(f.id) === nextSelected);
        const isBeneficiary = mainIdStr ? nextSelected !== mainIdStr : false;
        if (!mainMemberEnabled) {
          // If the profile is disabled, block both main member and beneficiaries
          setPatientEnableBeneficiary(false);
        } else if (isBeneficiary) {
          // Profile is enabled, so beneficiaries depend on enabledbeneficiary
          setPatientEnableBeneficiary(selected?.enabledbeneficiary || false);
        } else {
          // Main member uses profile enabled flag
          setPatientEnableBeneficiary(true);
        }

      } catch (err) {
        console.error("Profile load error:", err);
        alert("Failed to load patient profile");
      } finally {
        setLoading(false);
      }
    };
    if (profileNo) fetchProfileData();
  }, [profileNo]);

  // Update patientEnableBeneficiary when selectedPatient changes
  useEffect(() => {
    if (!selectedPatient || !familyData.length) return;
    const selected = familyData.find(f => String(f.id) === selectedPatient);
    const isBeneficiary = mainPatientId ? selectedPatient !== mainPatientId : false;
    if (!mainMemberEnabled) {
      // If the profile is disabled, block both main member and beneficiaries
      setPatientEnableBeneficiary(false);
    } else if (isBeneficiary) {
      // Profile is enabled, so beneficiaries depend on enabledbeneficiary
      setPatientEnableBeneficiary(selected?.enabledbeneficiary || false);
    } else {
      // Main member uses profile enabled flag
      setPatientEnableBeneficiary(true);
    }
  }, [selectedPatient, familyData, mainPatientId, mainMemberEnabled]);

  // Fetch scripts when patient changes
  useEffect(() => {
    if (!selectedPatient) return;

    const fetchScripts = async () => {
      try {
        // Use clinic script API if clinicOnly is true
        const scripts = clinicOnly 
          ? await fetchPatientClinicScripts(selectedPatient, userBranchCode)
          : await fetchPatientScripts(selectedPatient, userBranchCode);

        const selectedPatientStr = String(selectedPatient);
        const isSelectedBeneficiary = mainPatientId ? selectedPatientStr !== String(mainPatientId) : false;

        // Format for initialScriptData
        const formatted = scripts.map(s => ({
          // Use prescriptionid as the stable identifier for navigation / details fetching
          scriptNo: s.prescriptionid ?? s.prescriptionId ?? s.scriptNo ?? s.scriptno ?? s.clinicreference,
          scriptNumber: s.scriptNumber || s.clinicreference || s.prescriptionid,
          clinicReference: s.clinicreference || s.scriptNo || s.prescriptionid,
          type: s.prescriptiontype || s.type || 'clinic', // Use prescriptiontype from backend
          date: s.date || s.prescriptiondate,

          // Preserve chronic/repeat metadata for amend flows
          chronic: s.chronic ?? s.isChronic ?? false,
          isChronic: s.isChronic ?? s.chronic ?? false,
          // prefer numeric totals from the API if present
          repeatscript: s.repeatsTotal ?? s.repeatscript ?? 0,
          repeatScript: s.repeatsTotal ?? s.repeatscript ?? 0,
          repeatCount: s.repeatsTotal ?? s.repeatscript ?? 0,

          drugs: s.drugs ? s.drugs.map(d => ({
            drugName: d.drugName || d.drugname,
            packSize: "",
            icd10: "",
            strength: (d.drugName || d.drugname || "").includes("mg") ? (d.drugName || d.drugname).split(" ").pop() : "",
            quantity: d.quantity,
            nappiCode: "",
            instructions: d.instructions,
            costPrice: `R ${(d.quantity * 10).toFixed(2)}`,
          })) : [],
          repeat: s.repeat || s.repeatscript,
          total: s.total,
          paymentType: s.paymentType,
          scriptType: s.scriptType,

          // Status and amended flag from backend
          scriptStatus: s.scriptStatus || s.status,
          amended: s.amended === true,

          // MediKredit claim outcome for this specific script (medical aid only)
          claimStatus: s.claimStatus || null,
          claimAuthNumber: s.claimAuthNumber || null,
          claimNettPaid: s.claimNettPaid ?? null,
          claimCopay: s.claimCopay ?? null,

          followUp: s.followUp,

          patient: patientOptions.find(p => p.value === selectedPatient)?.label || "Unknown",
          practitioner: s.practitioner ? `${s.practitioner.firstname || ''} ${s.practitioner.surname || ''}`.trim() : "Dr. Unknown",
          pharmacist: s.pharmacist ? `${s.pharmacist.firstname || ''} ${s.pharmacist.surname || ''}`.trim() : "Pharmacist",
          medicalAid: medicalAidProvider,
          medicalAidNumber: medicalAidNumber,
          // Add beneficiary information from patient data
          isBeneficiary: isSelectedBeneficiary,
          mainMemberId: isSelectedBeneficiary ? Number(mainPatientId) : null,
          patientId: selectedPatient,
          profileNo: s.medicalaidnumber || profileNo, // Use medicalaidnumber (which contains profilenumber)
        }));

        setData(formatted); // Use formatted data instead of raw scripts
        initialScriptData = formatted;  // Update global
      } catch (err) {
        console.error("Scripts load error:", err);
        alert("Failed to load scripts");
      }
    };
    fetchScripts();
  }, [selectedPatient, mainPatientId, medicalAidProvider, medicalAidNumber, patientOptions, clinicOnly, userBranchCode]);

  const handleNewScript = () => {
    console.log("[FilterPatientScript] Navigating to new script", {
      profileNo,
      selectedPatient,
      selectedPatientLabel: patientOptions.find(p => p.value === selectedPatient)?.label || "",
    });
    navigate("/customer/dispensary/new-script", {
      state: { profileNo, patientId: selectedPatient }
    });
  };

  const handleMedicationHistory = () => {
    const patientName = patientOptions.find(p => p.value === selectedPatient)?.label || "";
    navigate(`/customer/dispensary/medication-history/${selectedPatient}`, {
      state: { patientName }
    });
  };

  // Table columns
  const scriptColumns = [
    { header: tableHeaders.scriptNo, accessor: "scriptNo", render: (_, row) => {
        const t = String(row.type || '').toLowerCase();
        if (t === 'process') return row.scriptNumber || '';
        if (t === 'clinic') return row.clinicReference || '';
        return row.scriptNo ?? row.scriptno ?? '';
      }
    },
    { header: tableHeaders.date, accessor: "date", render: (value) => formatDateTimeZA(value) },

    {
      header: "Medication(s)",
      render: (_, row) => row.drugs.map(d => (d.drugName || "").trim()).join(", "),
    },
    { header: "Repeat", accessor: "repeat" },
    { header: "Total", accessor: "total" },
    { header: "Payment Type", accessor: "paymentType" },
    { header: "Script Status", accessor: "scriptStatus" },
    {
      header: "Claim Status",
      render: (_, row) => row.claimStatus || (row.paymentType === "Medical Aid" ? "Not claimed" : "-"),
    },
    {
      header: "Claim Auth #",
      render: (_, row) => row.claimAuthNumber || "-",
    },
    {
      header: "Nett Paid",
      render: (_, row) => (row.claimNettPaid != null ? `R ${Number(row.claimNettPaid).toFixed(2)}` : "-"),
    },
    {
      header: "Amended",
      accessor: "amended",
      render: (_, row) => (row.amended ? "Yes" : "No"),
    },
    { header: "Patient Follow Up", accessor: "followUp" },
  ];

  const withExtraColumns = [
    ...scriptColumns,
    ...extraColumns,
    {
      header: tableHeaders.action,
      render: (_, row) => (
        <Button
          variant="primary"
          onClick={() => {
            const target = actionNavigateResolver
              ? actionNavigateResolver(row)
              : `${actionNavigatePath}/${row.scriptNo}`;
            
            // Handle different target formats
            if (typeof target === 'object' && target.pathname) {
              // Use the resolver's navigation object
              navigate(target.pathname, { 
                state: {
                  ...target.state,
                  // Merge with existing state if needed
                  profileNo: target.state?.profileNo || profileNo,
                  patientId: target.state?.patientId || selectedPatient
                }
              });
            } else {
              // Use the original string-based navigation
              navigate(target, { state: { profileNo, patientId: selectedPatient } });
            }
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const filteredColumns = withExtraColumns.filter(col => !columnsToHide.includes(col.accessor));

  // Pagination
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const paginatedData = data
    .filter(row => !hideOtc || String(row.type || '').toLowerCase() !== 'otc')
    .filter(row => !clinicOnly || String(row.type || '').toLowerCase().includes('clinic')) // Fix the clinicOnly filtering
    .filter(row => !scriptNoFilter || String(row.scriptNo || '').includes(scriptNoFilter))
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className={`${styles.container} ${containerClassName}`}>
      <Navbar />
      <main className={styles.main}>
        <h1 className={styles.title}>Patient Script Details</h1>

        <div className={styles.filterBox}>
          <div className={`${styles.filterRow} ${filterRowClassName}`}>
            <FormInput label="Medical Aid Provider" value={medicalAidProvider} readOnly={true} /> {/* Added readOnly */}
            <FormInput label="Medical Aid Number" value={medicalAidNumber} readOnly={true} /> {/* Added readOnly */}
            <Dropdown
              label="Patient"
              options={patientOptions.map(opt => opt.label)}
              value={patientOptions.find(opt => opt.value === selectedPatient)?.label || ''}
              onChange={(value) => {
                const selected = patientOptions.find(opt => opt.label === value);
                console.log("[FilterPatientScript] Patient dropdown changed", {
                  chosenLabel: value,
                  selectedValue: selected?.value || '',
                });
                setSelectedPatient(selected ? selected.value : '');
              }}
              placeholder="Select Patient"
            />
            <FormInput
              label="Enter Script No."
              value={scriptNoFilter}
              onChange={(e) => setScriptNoFilter(e.target.value)}
            />
            {!hideNewScriptButton && (
              <Button variant="primary" onClick={handleNewScript} disabled={!patientEnableBeneficiary} className={styles.newScriptButton}>
                New Script
              </Button>
            )}
            {!hideMedicationHistoryButton && (
              <Button variant="secondary" onClick={handleMedicationHistory} className={styles.newScriptButton}>
                Medication History
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <p className={styles.loading}>Loading patient scripts...</p>
        ) : (
          <>
            <Table columns={filteredColumns} data={paginatedData} />
            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}